import { z } from "zod";
import { ObjectiveSchema } from "./objectives.js";
/**
 * Task definitions are DATA, not code (ADR-001 constraint C5).
 *
 * Two consequences that the schema exists to enforce:
 *
 *  1. `presentation.narrative` is a first-class field, so the A-13 experiment
 *     (narrative vs. bare, `[FACT: S1]` — narrative correlates with weaker validity)
 *     is a config change rather than a release. The measurement layer and the
 *     narrative layer are separable by construction, per Phase 0 and v2 §20.
 *
 *  2. `measurement.calibrationVersion` is separate from the task `version`. Cosmetic
 *     changes bump `version`; anything touching scoring or difficulty MUST bump
 *     `calibrationVersion`, which breaks comparability and requires re-equating
 *     (v2 §18). Conflating them is how longitudinal records get silently corrupted.
 */
/**
 * What a task is about.
 *
 * The first two are what Phase 0 triaged as measurable. The rest arrived with the teacher
 * pivot (ADR-011) and are a different kind of thing: nobody claims a child "has" numeracy,
 * and no norm is implied. They name a curriculum area a preschool teacher already teaches,
 * so that an objective can say which part of their week it belongs to.
 *
 * `literacy` is the newest and the one that most obviously is not a construct. It is the
 * other half of the week — a preschool teacher spends it on numbers and on letters, and
 * until the alphabet game there was no word here for the second half. Naming it `literacy`
 * rather than `reading` is deliberate: nothing in the bundle asks a child to read, and a
 * label that promised reading would be describing an ability nobody here measures.
 */
export const ActiveConstructSchema = z.enum([
    "working_memory",
    "visuospatial",
    "numeracy",
    "reasoning",
    "literacy",
]);
export const ScoringSchema = z.enum([
    /** Highest reliably reproduced sequence length. */
    "span",
    /** Proportion correct at a fixed difficulty. */
    "accuracy",
]);
/**
 * **Every task teaches. There is no measurement mode, and that is a deletion rather than an
 * omission.**
 *
 * The obvious design was a `mode: "teach" | "measure"` flag, and it was written and then
 * removed: all fifteen tasks came out `teach`, so the enum had one live value and
 * `stopAfterErrors` — the field only the other branch read — was dead weight carrying a
 * rationale for behaviour nothing did. A flag with one value is a branch that is never taken
 * and a promise that a second behaviour exists somewhere.
 *
 * What replaced it is `stepDownAfter` and `maxTrials` below. A child who is stuck moves DOWN
 * rather than out, and a sitting ends by running out of trials rather than by running out of
 * patience (R-5). The teacher's view of how a child is doing is read from that same record —
 * there is no separate assessment sitting, and adding one would mean testing five-year-olds.
 */
export const TaskDefinitionSchema = z.object({
    id: z.string().regex(/^[a-z0-9-]+$/),
    /** Bumped for any change, cosmetic included. */
    version: z.string().regex(/^\d+\.\d+\.\d+$/),
    construct: ActiveConstructSchema,
    /** Tasks in one family are alternate measures of the same thing. */
    family: z.string().min(1),
    presentation: z.object({
        /** The A-13 lever. */
        narrative: z.boolean(),
        theme: z.string().min(1),
        /** Persian-first. Any language-sensitive content needs local norming (v2 §15),
         *  which is why active tasks are deliberately language-light. */
        locale: z.enum(["fa-IR"]),
        /** Must hold for language-light tasks — asserted per task, checked in tests. */
        languageIndependentStimulus: z.boolean(),
    }),
    measurement: z.object({
        scoring: ScoringSchema,
        /** Bump ONLY when scoring or difficulty semantics change. Breaks comparability. */
        calibrationVersion: z.string().regex(/^\d+$/),
        /** Alternate forms controlling practice effects (`[FACT: S14]`). At least two,
         *  or repeated play conflates learning with familiarity. */
        formIds: z.array(z.string().min(1)).min(2),
        minDifficulty: z.number().int().nonnegative(),
        maxDifficulty: z.number().int().positive(),
        /** Consecutive correct trials before difficulty rises. Also the mastery rule an
         *  objective is checked against, so the two can never disagree. */
        stepUpAfter: z.number().int().positive(),
        /**
         * Consecutive errors before the difficulty DROPS one level.
         *
         * A child who is stuck is in the wrong place, not finished. Stepping down keeps them
         * playing at a level they can do, which is where practice actually happens. This replaced
         * the old `stopAfterErrors`, which ended the task instead — correct for an instrument that
         * must not record frustration as inability (v2 §19), and wrong for an activity whose whole
         * job is the trial after the mistake.
         */
        stepDownAfter: z.number().int().positive(),
        /**
         * Trials in one sitting, after which the task ends as `completed`.
         *
         * This is the ONLY ordinary way a task ends, and that is the point: a session that ends by
         * running out of trials never ends on a run of failures.
         */
        maxTrials: z.number().int().positive(),
        trialTimeoutMs: z.number().int().positive(),
    }),
    params: z.record(z.union([z.number(), z.string(), z.boolean()])),
});
/**
 * A content bundle is the unit a child profile is PINNED to (ADR-002).
 * The client asks the server which bundle to load; it never loads "latest".
 */
export const ContentBundleSchema = z.object({
    bundleVersion: z.string().regex(/^\d+\.\d+\.\d+$/),
    /** Set once a bundle is pinned to any live profile; edits are then forbidden. */
    frozen: z.boolean(),
    tasks: z.array(TaskDefinitionSchema).min(1),
    /**
     * The objectives these tasks carry (ADR-011).
     *
     * In the bundle rather than in a table of their own, because an objective is a window of
     * levels in a named task and both of those are bundle-local. A student's record of what they
     * have learnt is only readable against the objective set it was recorded under — move the
     * levels and "mastered adding to ten" silently starts meaning something else.
     */
    objectives: z.array(ObjectiveSchema).min(1),
});
/** Server tells the client which bundle to run. */
export const BundleAssignmentSchema = z.object({
    profileId: z.string().uuid(),
    bundleVersion: z.string().min(1),
    /** Set when the profile was moved between bundles, with the equating rationale.
     *  Null for a first assignment. */
    reEquatedFrom: z.string().min(1).nullable(),
    reEquatingNote: z.string().nullable(),
});
/** Comparability check used by tests and by the re-equating tool. */
export function comparable(a, b) {
    return (a.id === b.id &&
        a.measurement.calibrationVersion === b.measurement.calibrationVersion);
}
/**
 * The A-13 arm check. Two presentation variants are a valid experiment only if they
 * are scoring-identical and differ solely in presentation. If their measurement
 * blocks diverge at all, a difference in outcome is uninterpretable — it could be
 * the narrative or it could be the scoring, and the experiment answers nothing.
 */
export function validPresentationArms(a, b) {
    return (a.family === b.family &&
        a.construct === b.construct &&
        a.presentation.narrative !== b.presentation.narrative &&
        JSON.stringify(a.measurement) === JSON.stringify(b.measurement) &&
        JSON.stringify(a.params) === JSON.stringify(b.params));
}
//# sourceMappingURL=tasks.js.map